import { Controller } from "@hotwired/stimulus"

// Export modal controller — format picker, conditional options, submit.
// The format cards + option checkboxes are visual-state toggles; on
// submit the controller builds a hidden form, posts it with the current
// dashboard filters, and lets the browser stream the response so the
// file downloads (or opens in a new tab for PDFs) without leaving the
// dashboard. The existing dropdown-based PDF/XLSX buttons used the
// same POST endpoint — we just gather richer options now.
export default class extends Controller {
  static targets = [
    "formatCard", "optionsSection", "xlsxNote",
    "insightsCheck", "aiSummaryCheck", "chartsCheck", "commentaryCheck",
    "chartRangeGroup", "rangeBtn",
    "submitBtn", "submitLabel", "submitSpinner", "projectCount"
  ]
  static values = {
    submitUrl: String,
    filters: Object
  }

  connect() {
    this._format = "pdf"
    this._range = 6
  }

  selectFormat(event) {
    event.preventDefault()
    const card = event.currentTarget
    const format = card.dataset.format
    this._format = format

    this.formatCardTargets.forEach(c => {
      const selected = c.dataset.format === format
      c.classList.toggle("border-[var(--portfolio-primary)]", selected)
      c.classList.toggle("bg-[var(--portfolio-primary)]/5", selected)
      c.classList.toggle("border-gray-200", !selected)
      const check = c.querySelector("[data-portfolio-export-target^='checkmark-']")
      if (check) check.classList.toggle("hidden", !selected)
    })

    // XLSX hides all content options; PDF/PPTX show them.
    const isXlsx = format === "xlsx"
    this.optionsSectionTarget.classList.toggle("hidden", isXlsx)
    this.xlsxNoteTarget.classList.toggle("hidden", !isXlsx)
  }

  selectRange(event) {
    event.preventDefault()
    const btn = event.currentTarget
    this._range = parseInt(btn.dataset.range, 10)
    this.rangeBtnTargets.forEach(b => {
      const selected = b.dataset.range === btn.dataset.range
      b.classList.toggle("border-[var(--portfolio-primary)]", selected)
      b.classList.toggle("text-[var(--portfolio-primary)]", selected)
      b.classList.toggle("bg-[var(--portfolio-primary)]/5", selected)
      b.classList.toggle("font-medium", selected)
      b.classList.toggle("border-gray-200", !selected)
      b.classList.toggle("text-gray-600", !selected)
      b.classList.toggle("hover:bg-gray-50", !selected)
    })
  }

  toggleChartsRange() {
    const enabled = this.chartsCheckTarget.checked
    this.chartRangeGroupTarget.classList.toggle("opacity-40", !enabled)
    this.chartRangeGroupTarget.classList.toggle("pointer-events-none", !enabled)
  }

  // Close the export modal, open the schedule-report modal.
  openSchedule(event) {
    event.preventDefault()
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
    const scheduleModal = document.getElementById("portfolio-schedule-report-modal")
    if (scheduleModal) {
      scheduleModal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  async submit(event) {
    event.preventDefault()
    if (this.submitBtnTarget.disabled) return

    const options = {
      include_insights: this._boolOrDefault("insightsCheck", true),
      include_ai_summary: this._boolOrDefault("aiSummaryCheck", false),
      include_charts: this._boolOrDefault("chartsCheck", true),
      include_commentary: this._boolOrDefault("commentaryCheck", true),
      trend_range: this._range
    }

    // Mixpanel is fired server-side from the controller action so it
    // reflects the formats + options that actually rendered, not what the
    // client intended. See companies_controller.rb#portfolio_dashboard_export.

    this._renderBusyState()

    try {
      const blob = await this._fetchExport(options)
      this._downloadBlob(blob)
      // Close modal on successful download — user feedback that the job is done.
      this.element.dispatchEvent(new CustomEvent("modal:close-request", { bubbles: true }))
      const m = this.element
      m.classList.add("hidden")
      document.body.style.overflow = ""
    } catch (err) {
      console.error("Export failed", err)
      alert("Export failed — please try again.")
    } finally {
      this._resetBusyState()
    }
  }

  _boolOrDefault(targetName, fallback) {
    const hasTarget = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`
    if (!this[hasTarget]) return fallback
    return this[`${targetName}Target`].checked
  }

  _renderBusyState() {
    this.submitBtnTarget.disabled = true
    this.submitLabelTarget.textContent = "Generating…"
    this.submitSpinnerTarget.classList.remove("hidden")
  }

  _resetBusyState() {
    this.submitBtnTarget.disabled = false
    this.submitLabelTarget.textContent = "Generate"
    this.submitSpinnerTarget.classList.add("hidden")
  }

  async _fetchExport(options) {
    const params = new URLSearchParams()
    params.set("format_type", this._format)
    params.set("include_insights", options.include_insights ? "1" : "0")
    params.set("include_ai_summary", options.include_ai_summary ? "1" : "0")
    params.set("include_charts", options.include_charts ? "1" : "0")
    params.set("include_commentary", options.include_commentary ? "1" : "0")
    params.set("trend_range", String(options.trend_range))

    const filters = this.filtersValue || {}
    Object.entries(filters).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return
      if (Array.isArray(v)) {
        v.forEach(item => params.append(`${k}[]`, item))
      } else {
        params.set(k, v)
      }
    })

    const csrf = document.querySelector("meta[name=csrf-token]")?.content
    const res = await fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrf || "",
        "Accept": "*/*"
      },
      body: params.toString(),
      credentials: "same-origin"
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return { blob: await res.blob(), contentDisposition: res.headers.get("Content-Disposition") || "" }
  }

  _downloadBlob({ blob, contentDisposition }) {
    // Prefer server-provided filename; fall back to a sensible default.
    const match = /filename="?([^";]+)"?/.exec(contentDisposition || "")
    const filename = match?.[1] || `portfolio_dashboard.${this._format}`

    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    // PDFs preview inline; _blank lets PDFs open in a tab, downloads still work
    // for octet-stream types (PPTX/XLSX) because the `download` attr wins.
    if (this._format === "pdf") a.target = "_blank"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}
